import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  DwComponent,
  DwComponentType,
  DwExpressionService,
  DwMetaDataFormApi,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwOrmDataService,
  DwOrmDataServiceToken,
  DwSectionBaseComponent,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken,
  DwUiConfigRegistryService
} from '@devwareapps/devware-cap';
import { combineLatest, of, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import {
  AddressEntity,
  AppMetaData,
  AppMetaDataItemNames,
  AppMetaDataLookups,
  FlightSchoolEntity
} from '../../../../meta-data/app-meta-data.service';

interface BillingProfileViewModel {
  email: string;
  emailIsMissing: boolean;
  addressLine1: string;
  addressLine2: string;
  localityLine: string;
  country: string;
  addressIsComplete: boolean;
}

@DwComponent({
  key: 'flight-school-billing-profile-form-section',
  name: 'Flight School Billing Profile - Form Section',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.FlightSchool,
  isGlobal: false
})
@Component({
  selector: 'app-billing-profile-form-section',
  templateUrl: './billing-profile-form-section.component.html',
  styleUrls: ['./billing-profile-form-section.component.scss']
})
export class BillingProfileFormSectionComponent extends DwSectionBaseComponent implements OnInit, OnDestroy {
  viewModel: BillingProfileViewModel;

  private readonly formApi: DwMetaDataFormApi;
  private readonly destroy$ = new Subject<void>();
  private billingAddress: AddressEntity;
  private loadedBillingAddressId: number;
  private viewModelVersion = 0;

  constructor(
    dwExpressionService: DwExpressionService,
    dwFormStateService: DwMetaDataFormStateService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    @Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService
  ) {
    super(
      dwExpressionService,
      dwUiConfigRegistryService,
      uiMetaDataConfig,
      dwMetaDataService
    );
    this.formApi = dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    this.runAfterFormDataLoaded(() => this.syncFromForm());
    this.formApi.getFormDataChanges<FlightSchoolEntity>()
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.syncFromForm());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private syncFromForm(): void {
    const school = this.formApi.getFormData<FlightSchoolEntity>();
    const addressId = school?.BillingAddressId;

    if (school?.BillingAddress) {
      this.billingAddress = school.BillingAddress;
      this.loadedBillingAddressId = addressId;
    } else if (addressId && addressId !== this.loadedBillingAddressId) {
      this.loadBillingAddress(addressId);
    } else if (!addressId) {
      this.billingAddress = undefined;
      this.loadedBillingAddressId = undefined;
    }

    this.buildViewModel(school, this.billingAddress);
  }

  private loadBillingAddress(addressId: number): void {
    this.loadedBillingAddressId = addressId;

    const query = AppMetaData.Address.CreateQueryBuilder();
    query.addFilterAnd(filter => filter.Equal(attribute => attribute.AddressId, addressId));
    this.dwOrmDataService.executeQuerySingle<AddressEntity>(query)
      .pipe(takeUntil(this.destroy$))
      .subscribe(address => {
        const currentSchool = this.formApi.getFormData<FlightSchoolEntity>();

        if (currentSchool?.BillingAddressId !== addressId) {
          return;
        }

        this.billingAddress = address;
        this.buildViewModel(currentSchool, address);
      });
  }

  private buildViewModel(school: FlightSchoolEntity, address: AddressEntity): void {
    const version = ++this.viewModelVersion;
    const stateDisplay$ = address?.StateProvinceId
      ? this.dwMetaDataService.getLookupDisplay(
        AppMetaDataLookups.StateProvinceAllItems,
        address.StateProvinceId
      )
      : of('');
    const countryDisplay$ = address?.CountryId
      ? this.dwMetaDataService.getLookupDisplay(
        AppMetaDataLookups.CountryAllItems,
        address.CountryId
      )
      : of('');

    combineLatest([stateDisplay$, countryDisplay$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([state, country]) => {
        if (version !== this.viewModelVersion) {
          return;
        }

        this.setViewModel(school, address, state, country);
      });
  }

  private setViewModel(
    school: FlightSchoolEntity,
    address: AddressEntity,
    state: string,
    country: string
  ): void {
    const email = school?.BillingContactEmail?.trim();
    const postalCode = address?.PostalCode?.trim();

    this.viewModel = {
      email: email || 'No billing email has been set up.',
      emailIsMissing: !email,
      addressLine1: address?.StreetAddress1?.trim() || '',
      addressLine2: address?.StreetAddress2?.trim() || '',
      localityLine: [address?.City?.trim(), state].filter(value => !!value).join(', ')
        + (postalCode ? ` ${postalCode}` : ''),
      country,
      addressIsComplete: !!address?.StreetAddress1?.trim()
        && !!address?.City?.trim()
        && !!postalCode
        && !!country
    };
  }
}
